import React, { useMemo, useState } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';
import useReveal from '../hooks/useReveal';
import TokenRevealText from '../components/TokenRevealText';

const Contact = () => {
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  useReveal();

  const EMAILJS_SERVICE_ID = 'service_1v8wvfa';
  const EMAILJS_TEMPLATE_CONTACT = 'template_hd1ymdl';
  const EMAILJS_PUBLIC_KEY = 'QX39afSpRf-CsHOR0';

  const validate = (values) => {
    const next = {};
    const name = (values.name || '').trim();
    const email = (values.email || '').trim();
    const phone = (values.phone || '').trim();
    const message = (values.message || '').trim();

    if (!name) next.name = 'Full name is required.';

    if (!email) {
      next.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = 'Enter a valid email address.';
    }

    // Optional phone, but if provided should be reasonable.
    if (phone && !/^[+()\d\s-]{7,20}$/.test(phone)) {
      next.phone = 'Enter a valid phone number.';
    }

    if (!message) next.message = 'Message is required.';
    return next;
  };

  const errors = useMemo(() => validate(form), [form]);
  const canSubmit = Object.keys(errors).length === 0;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setAttempted(true);
    setTouched({
      name: true,
      company: true,
      email: true,
      phone: true,
      message: true,
    });

    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSending(true);
    setSendError('');

    const templateParams = {
      name: form.name,
      email: form.email,
      message: form.message,
      title: form.company || 'Contact request',
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_CONTACT,
        templateParams,
        EMAILJS_PUBLIC_KEY,
      );

      setSubmitted(true);
    } catch (error) {
      setSendError('Sorry, something went wrong. Please try again shortly.');
    } finally {
      setIsSending(false);
    }
  };

  const showError = (field) => (attempted || touched[field]) && errors[field];

  return (
    <main className="contact-page page-enter">
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container reveal">
          <p className="section-eyebrow-light"><TokenRevealText text="Get In Touch" speed="medium" /></p>
          <h1><TokenRevealText text="Contact Us" speed="medium" /></h1>
          <p>
            <TokenRevealText
              text="We would love to hear from you. Reach out to discuss your project requirements."
              speed="medium"
            />
          </p>
        </div>
      </section>

      {/* ===== CONTACT MAIN ===== */}
      <section className="section contact-main-section">
        <div className="container contact-layout">
          {/* Contact Info */}
          <div className="contact-info-col reveal reveal-left">
            <p className="section-eyebrow"><TokenRevealText text="Our Details" speed="medium" /></p>
            <h2 className="section-title"><TokenRevealText text="Get in Touch" speed="medium" /></h2>
            <div className="divider" />
            <p style={{ color: 'var(--text-gray)', marginBottom: '32px', lineHeight: '1.7' }}>
              <TokenRevealText
                text="We are always ready to discuss new projects, challenges, and how we can help transform your ideas into precision engineered products."
                speed="medium"
              />
            </p>

            <div className="contact-info-cards">
              <div className="ci-card reveal reveal-scale reveal-d1">
                <div className="ci-icon ci-icon-addr">L</div>
                <div>
                  <strong><TokenRevealText text="Address" speed="medium" /></strong>
                  <p>C2P Engineering Solutions Pvt. Ltd.<br />
                  Survey No. 23/2, Part-A, Gundala Pochampally, Medchal<br />
                  Hyderabad – 500014</p>
                </div>
              </div>

              <div className="ci-card reveal reveal-scale reveal-d2">
                <div className="ci-icon ci-icon-phone">P</div>
                <div>
                  <strong><TokenRevealText text="Phone" speed="medium" /></strong>
                  <p>
                    <a href="tel:+919704403522">+91 9704403522</a><br />
                    <a href="tel:+919000103522">+91 9000103522</a>
                  </p>
                </div>
              </div>

              <div className="ci-card reveal reveal-scale reveal-d3">
                <div className="ci-icon ci-icon-email">@</div>
                <div>
                  <strong><TokenRevealText text="Email" speed="medium" /></strong>
                  <p><a href="mailto:info@analogicgroup.com">info@analogicgroup.com</a></p>
                </div>
              </div>

              <div className="ci-card reveal reveal-scale reveal-d4">
                <div className="ci-icon ci-icon-hours">H</div>
                <div>
                  <strong><TokenRevealText text="Business Hours" speed="medium" /></strong>
                  <p>Monday – Saturday<br />9:30 AM – 6:00 PM IST</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-col reveal reveal-right">
            <div className="form-card reveal reveal-scale">
              <h3><TokenRevealText text="Send Us a Message" speed="medium" /></h3>
              <p><TokenRevealText text="Fill in the form below and we will get back to you within 24 hours." speed="medium" /></p>

              {submitted ? (
                <div className="success-msg">
                  <div className="success-icon">&#10003;</div>
                  <h4><TokenRevealText text="Message Sent!" speed="medium" /></h4>
                  <p><TokenRevealText text="Thank you for reaching out. We will contact you shortly." speed="medium" /></p>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      setSubmitted(false);
                      setAttempted(false);
                      setTouched({});
                      setForm({ name: '', company: '', email: '', phone: '', message: '' });
                      setSendError('');
                    }}
                    style={{ marginTop: '12px' }}
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name"><TokenRevealText text="Full Name *" speed="medium" /></label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Your full name"
                        aria-invalid={Boolean(showError('name'))}
                        className={showError('name') ? 'has-error' : ''}
                      />
                      {showError('name') ? <div className="field-error">{errors.name}</div> : null}
                    </div>
                    <div className="form-group">
                      <label htmlFor="company"><TokenRevealText text="Company" speed="medium" /></label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Your company name"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email"><TokenRevealText text="Email Address *" speed="medium" /></label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="your@email.com"
                        aria-invalid={Boolean(showError('email'))}
                        className={showError('email') ? 'has-error' : ''}
                      />
                      {showError('email') ? <div className="field-error">{errors.email}</div> : null}
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone"><TokenRevealText text="Phone Number" speed="medium" /></label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="+91 XXXXX XXXXX"
                        aria-invalid={Boolean(showError('phone'))}
                        className={showError('phone') ? 'has-error' : ''}
                      />
                      {showError('phone') ? <div className="field-error">{errors.phone}</div> : null}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="message"><TokenRevealText text="Message *" speed="medium" /></label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Tell us about your project or requirements..."
                      rows={5}
                      aria-invalid={Boolean(showError('message'))}
                      className={showError('message') ? 'has-error' : ''}
                    />
                    {showError('message') ? <div className="field-error">{errors.message}</div> : null}
                  </div>
                  <button
                    type="submit"
                    className="btn-primary form-submit"
                    disabled={!canSubmit || isSending}
                  >
                    {isSending ? 'Sending...' : <TokenRevealText text="Send Message →" speed="medium" />}
                  </button>
                  {sendError ? <div className="field-error">{sendError}</div> : null}
                  {!canSubmit && attempted ? (
                    <div className="form-hint">Please fix the highlighted fields.</div>
                  ) : null}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== MAP SECTION ===== */}
      <section className="section section-alt map-section contact-map-section">
        <div className="container">
          <div className="reveal" style={{ textAlign: 'center', marginBottom: '40px' }}>
            <p className="section-eyebrow"><TokenRevealText text="Find Us" speed="medium" /></p>
            <h2 className="section-title centered"><TokenRevealText text="Our Location" speed="medium" /></h2>
            <div className="divider" style={{ margin: '12px auto 0' }} />
          </div>
          <a 
            href="https://www.google.com/maps/dir//C2P+Engineering+Solutions+Pvt+Ltd,+Sy.+No.+23%2F2,+Part+%E2%80%98A,+Gundlapochampally,+Hyderabad,+Telangana+500100/@17.4514633,78.6902064,13z/data=!4m8!4m7!1m0!1m5!1m1!1s0x3bcb858ee968fd91:0xfa96a56f34731d1a!2m2!1d78.4831254!2d17.583525?entry=ttu"
            target="_blank"
            rel="noopener noreferrer"
            className="map-link"
          >
            <div className="map-container reveal">
              <iframe
                title="C2P Engineering Solutions Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3797.8!2d78.4804!3d17.5835!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb858ee968fd91%3A0xfa96a56f34731d1a!2sC2P%20Engineering%20Solutions%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1709913600000!5m2!1sen!2sin"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="map-overlay">
                <span className="map-overlay-text">Click to open in Google Maps</span>
              </div>
            </div>
          </a>
        </div>
      </section>
    </main>
  );
};

export default Contact;